import React from 'react';
import { cn } from "@/lib/utils";
import { Table } from '../table';
import TableHeader from '../TableCard/TableHeader';
import TableCell from '../TableCard/TableCell';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ColumnConfigItem } from '@/hooks/column-config/types';

interface FullTableProps<T> {
  data: T[];
  columns: ColumnConfigItem[];
  className?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  onRowClick?: (item: T) => void;
}

function FullTable<T extends Record<string, any>>({
  data = [],
  columns = [],
  className,
  emptyMessage = "No data available",
  isLoading = false,
  onRowClick,
}: FullTableProps<T>) {
  const visibleColumns = columns.filter(col => col.visible !== false);

  return (
    <div className={cn(
      "rounded-md border overflow-hidden",
      className
    )}>
      <ScrollArea className="h-[500px]">
        <Table>
          <thead className="bg-muted/50">
            <tr>
              {visibleColumns.map((column) => (
                <TableHeader key={column.key}>
                  {column.header}
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length}
                  className="text-center py-8 text-muted-foreground"
                >
                  {isLoading ? "Loading data..." : emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, rowIndex) => (
                <tr 
                  key={item.id || `row-${rowIndex}`} 
                  className={cn(
                    "transition-colors hover:bg-muted/50",
                    onRowClick ? "cursor-pointer" : ""
                  )}
                  onClick={() => onRowClick?.(item)}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={`${item.id || rowIndex}-${column.key}`}
                    >
                      {typeof column.options?.render === 'function' 
                        ? column.options.render(item[column.key], item) 
                        : item[column.key]}
                    </TableCell>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </ScrollArea>
    </div>
  );
}

export default FullTable;
